import { AppError } from '@/shared/lib/appError'

export const isNavigatorOnline = (): boolean =>
  typeof navigator === 'undefined' ? true : navigator.onLine

const networkErrorPatterns = [
  'networkerror',
  'network request failed',
  'failed to fetch',
  'fetch failed',
  'offline',
  'load failed',
]

export const isLikelyOfflineError = (error: unknown): boolean => {
  if (!isNavigatorOnline()) {
    return true
  }

  if (error instanceof AppError) {
    if (error.code === 'NETWORK' || error.status === 0 || error.status === 503) {
      return true
    }
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return networkErrorPatterns.some((pattern) => message.includes(pattern))
  }

  return false
}

const authSessionErrorPatterns = [
  'session',
  'refresh',
  'missing',
  'auth',
  'invalid',
  'jwt',
  'token',
  'expired',
  'lock',
  'lockmanager',
  'timed out',
]

/** Erros de sessão/auth que podem ser transitórios (ex.: lock, race). */
export const isLikelyAuthSessionError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return authSessionErrorPatterns.some((pattern) => message.includes(pattern))
  }
  return false
}

export const getErrorMessage = (error: unknown, fallback = 'Falha ao enviar alteração.'): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message
  }
  return fallback
}

const NETWORK_RETRY_DELAYS_MS = [1000, 3000, 6000]

/**
 * Retries the given async operation when it fails with a transient network error
 * (e.g. "failed to fetch"). Useful for import/bulk operations that can fail due
 * to brief connectivity hiccups.
 */
export const retryOnNetworkError = async <T>(
  fn: () => Promise<T>,
  maxRetries = 3,
): Promise<T> => {
  let lastError: unknown
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      return await fn()
    } catch (err) {
      lastError = err
      if (attempt < maxRetries && isLikelyOfflineError(err)) {
        const delay = NETWORK_RETRY_DELAYS_MS[attempt] ?? 3000
        await new Promise((resolve) => setTimeout(resolve, delay))
        continue
      }
      throw err
    }
  }
  throw lastError
}

/**
 * Retries when the operation fails with network or auth/session errors (e.g. lock contention).
 * Use for import flows where auth can fail transiently.
 */
export const retryOnNetworkOrAuthError = async <T>(
  fn: () => Promise<T>,
  maxRetries = 3,
): Promise<T> => {
  let lastError: unknown
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      return await fn()
    } catch (err) {
      lastError = err
      const isRetryable =
        attempt < maxRetries &&
        (isLikelyOfflineError(err) || isLikelyAuthSessionError(err))
      if (isRetryable) {
        const delay = NETWORK_RETRY_DELAYS_MS[attempt] ?? 3000
        await new Promise((resolve) => setTimeout(resolve, delay))
        continue
      }
      throw err
    }
  }
  throw lastError
}
